// Sizes in CSS pixels unless the name says otherwise.

// ─── Axis text (po-chart-visual-spec.md §12.2, §12.3; 12 px reads better than PO's 11) ─
export const AXIS_FONT_SIZE_PX = 12;
export const AXIS_FONT_WEIGHT_REGULAR = 400;
export const AXIS_FONT_WEIGHT_BOLD = 600;

// ─── Layout ─────────────────────────────────────────────────────────────────
export const TIME_AXIS_HEIGHT_PX = 28;
// Space on both sides of the price labels; the axis width follows the longest label (E16)
export const PRICE_AXIS_PADDING_X_PX = 8;
// Price badges on the axis are tags pointing at their price (moon price-badge-zoom.png):
// the pointed left edge takes this much of the axis, labels start after it
export const PRICE_BADGE_ARROW_WIDTH_PX = 6;
export const PRICE_BADGE_HEIGHT_PX = 20;
// Right end fully round like a pill; the corners next to the point slightly rounded,
// the point itself stays sharp (moon.com)
export const PRICE_BADGE_RADIUS_PX = PRICE_BADGE_HEIGHT_PX / 2;
export const PRICE_BADGE_SHOULDER_RADIUS_PX = 6;
export const PRICE_AXIS_MIN_WIDTH_PX = 40;
// The axis width settles in one extra pass when the labels change it
export const LAYOUT_PASSES = 2;

// ─── Label density (E16: labels never overlap) ──────────────────────────────
export const PRICE_LABEL_MIN_SPACING_PX = 30;
export const TIME_LABEL_GAP_PX = 16;

// ─── Time scale ─────────────────────────────────────────────────────────────
// Default zoom as in TradingView; double click on the time axis returns to it
export const DEFAULT_BAR_SPACING_PX = 6;
// Empty bars between the last bar and the price axis (as in the TV reference)
export const DEFAULT_RIGHT_OFFSET_BARS = 10;
// Zoom limits only keep candles from collapsing to nothing or getting huge
// (about as on Moon: ~1 px at max zoom-out, ~50 px at max zoom-in)
export const MIN_BAR_SPACING_PX = 1;
export const MAX_BAR_SPACING_PX = 50;
// Pan stops when only this many bars are left in view at the pane edge
export const MIN_VISIBLE_BARS = 2;

// ─── High / low labels (po-chart-visual-spec.md §12.6) ──────────────────────
export const HIGH_LOW_LABEL_HEIGHT_PX = 20;
export const HIGH_LOW_LABEL_PADDING_X_PX = 7;
export const HIGH_LOW_LABEL_RADIUS_PX = 3;
// Between the wick and the label
export const HIGH_LOW_LABEL_GAP_PX = 4;
// A label that would cover the bar timer moves left of it, this far away
export const HIGH_LOW_TIMER_SPACING_PX = 4;

// ─── Bar timer (po-chart-visual-spec.md §12.5, 15-current-price-timer-crosshair.png) ─
// From the right edge of the last bar body to the text
export const COUNTDOWN_BAR_GAP_PX = 12;
// The timeframe line is above the last price, the countdown line below it
export const COUNTDOWN_LINE_HEIGHT_PX = 14;
export const COUNTDOWN_LINE_GAP_PX = 2;
// The timer stays inside the pane, this far from the price axis
export const COUNTDOWN_EDGE_PADDING_PX = 4;
// The countdown is redrawn this long after a whole second, when its value has surely changed
export const COUNTDOWN_TICK_DELAY_MS = 5;

// ─── Price scale ────────────────────────────────────────────────────────────
// The React toolbar and clock over the top-left corner of the chart
// (ChartToolbar, ChartClock): $chart-overlay-offset 11 + button 34 + 5 + clock 10 + 20
export const TOOLBAR_OVERLAY_HEIGHT_PX = 80;
// Bottom of the toolbar button row: $chart-overlay-offset 11 + button 34
export const TOOLBAR_ROW_BOTTOM_PX = 45;
// Free space above the highest and below the lowest bar: on top the high label sits
// under the toolbar button row, level with the clock; at the bottom the low label
// and some air (01-candles-m1-positions.jpg)
export const PANE_MARGIN_TOP_PX =
  TOOLBAR_ROW_BOTTOM_PX + HIGH_LOW_LABEL_GAP_PX + HIGH_LOW_LABEL_HEIGHT_PX + HIGH_LOW_LABEL_GAP_PX;
export const PANE_BOTTOM_AIR_PX = 5;
export const PANE_MARGIN_BOTTOM_PX =
  HIGH_LOW_LABEL_HEIGHT_PX + HIGH_LOW_LABEL_GAP_PX + PANE_BOTTOM_AIR_PX;
// On a low chart each margin takes at most this share of the pane height
export const PANE_MARGIN_MAX_SHARE = 0.4;
// Half-height of the range around a flat price (relative to the price)
export const FLAT_RANGE_PADDING_RATIO = 0.001;

// ─── Series (engine-ref-images 01, 04–06) ───────────────────────────────────
// Candle body as a share of the bar spacing
export const CANDLE_BODY_WIDTH_RATIO = 0.8;
// OHLC bar (PO, Bars BTC M1): stem, open and close ticks together, as a share of the bar spacing
export const OHLC_BAR_WIDTH_RATIO = 0.9;
// Shadow style Thick (E18): the wick is this wide, but leaves this much of the body
export const THICK_WICK_WIDTH_PX = 3;
export const THICK_WICK_BODY_MARGIN_PX = 2;
// Line chart: the line along closes and the dot on its last point
export const LINE_SERIES_WIDTH_PX = 2;
// Segment ends overlap by half the width, so the joints have no gaps
export const LINE_SERIES_CAP_RATIO = 0.5;
export const LAST_POINT_DOT_RADIUS_PX = 3;

// ─── Last price (po-chart-visual-spec.md §12.4) ─────────────────────────────
export const LAST_PRICE_LINE_WIDTH_PX = 1;
export const LAST_PRICE_DASH_PX = [6, 4];

// ─── Animation (E5, po-chart-visual-spec.md §12.8) ──────────────────────────
// A tick moves the last bar for about the average interval between price
// changes, so the movement does not stop between ticks. Measured on the
// Binance futures stream behind streaming.js (100 ms throttle), 2026-10-29:
// BTC, ETH ~200 ms, SOL ~450 ms, DOT ~1 s.
export const TICK_ANIMATION_DEFAULT_MS = 250;
export const TICK_ANIMATION_MIN_MS = 120;
export const TICK_ANIMATION_MAX_MS = 1000;
// Weight of the newest interval in the smoothed one
export const TICK_INTERVAL_SMOOTHING = 0.25;
// The view slides by one bar when it follows a new bar; also scrolling to the latest bars and a new visible span
export const VIEW_SCROLL_ANIMATION_MS = 300;
// While the view follows time, a new bar that lands further than this (bars) from its opened room slides there
export const VIEW_FOLLOW_MIN_JUMP_BARS = 0.01;
// Autoscale settles over this time when other bars come into view
export const AUTOSCALE_ANIMATION_MS = 300;
// Ring around the last point of the Line chart: grows from the dot and fades (04-line-area.jpg)
export const PULSE_RING_MAX_RADIUS_PX = 10;
export const PULSE_RING_WIDTH_PX = 1;
export const PULSE_RING_PERIOD_MS = 1500;

// ─── Data ───────────────────────────────────────────────────────────────────
// Bars requested by the first getBars call
export const INITIAL_BAR_COUNT = 500;
// Bars requested by every later getBars call for older history
export const OLDER_PAGE_BAR_COUNT = 500;
// Older history is requested when fewer than this many screens of bars are left of the view
export const HISTORY_PRELOAD_SCREENS = 1;

// ─── 15S / 30S first view ──────────────────────────
// The first time a seconds timeframe is opened, the last 15 minutes are visible
export const SECONDS_FIRST_VIEW_MS = 15 * 60 * 1000;
export const SECONDS_FIRST_VIEW_RIGHT_OFFSET_BARS = 5;

// ─── Crosshair (02-crosshair.jpg, po-chart-visual-spec.md §12.7) ─────────────
export const CROSSHAIR_LINE_WIDTH_PX = 1;
export const CROSSHAIR_DASH_PX = [5, 4];
export const AXIS_LABEL_HEIGHT_PX = 20;
export const AXIS_LABEL_PADDING_X_PX = 6;
export const AXIS_LABEL_RADIUS_PX = 3;

// ─── Indicator panes (po-chart-visual-spec.md §5.3) ─────────────────────────
// An oscillator pane under the price chart (PO), Expand makes it taller
export const INDICATOR_PANE_HEIGHT_PX = 120;
export const INDICATOR_PANE_EXPANDED_HEIGHT_PX = 180;
// D24: panes shrink down to this before the ones that do not fit are scrolled to
export const INDICATOR_PANE_MIN_HEIGHT_PX = 60;
// D24: oscillator panes together leave at least this share of the chart to the price pane
export const MAIN_PANE_MIN_SHARE = 0.4;
// Air above the highest and below the lowest value of an oscillator
export const INDICATOR_PANE_MARGIN_PX = 8;

// ─── Indicator shapes on the chart ──────────────────────────────────────────
// Background of a channel (Bollinger Bands…): its colour at this opacity
// (the Bollinger Bands fill on 04-indicators-menu-legend-pane.jpg)
export const INDICATOR_FILL_OPACITY = 0.1;
// Parabolic SAR dot (PO: 3 px) and Fractal arrowhead (PO: 8 px) grow with
// the line width of the Styles tab (1…5 px): diameter or size = base + width × this
export const INDICATOR_SHAPE_PX_PER_WIDTH = 2;
export const INDICATOR_DOT_BASE_PX = 1;
export const INDICATOR_MARKER_BASE_PX = 6;
// Space between a Fractal arrowhead and the high / low it marks
export const INDICATOR_MARKER_GAP_PX = 4;
// Signal labels of indicators (SuperTrend Buy / Sell): a rounded box in the
// colour of its line, this far from the line value
export const INDICATOR_LABEL_FONT_SIZE_PX = 11;
export const INDICATOR_LABEL_FONT_WEIGHT = 500;
export const INDICATOR_LABEL_HEIGHT_PX = 16;
export const INDICATOR_LABEL_PADDING_X_PX = 4;
export const INDICATOR_LABEL_RADIUS_PX = 3;
export const INDICATOR_LABEL_GAP_PX = 4;

// ─── Gestures ───────────────────────────────────────────────────────────────
// A pointer that moved less than this is a tap, a click or a long press
export const TAP_MAX_MOVE_PX = 5;
export const DOUBLE_TAP_INTERVAL_MS = 500;
export const DOUBLE_TAP_MAX_DISTANCE_PX = 20;
// Touch held still this long shows the crosshair (as in lightweight-charts)
export const LONG_PRESS_MS = 240;
// One zoom step (keyboard ↑ ↓) changes the bar spacing by about 10 %
export const WHEEL_ZOOM_PER_NOTCH = Math.log(1.1);
// Trackpad pinch comes as Ctrl + wheel with small deltas
export const PINCH_WHEEL_DELTA_LIMIT = 10;
export const PINCH_WHEEL_ZOOM_PER_DELTA = 0.01;
// Wheel deltas in lines and pages are converted to pixels
export const WHEEL_LINE_HEIGHT_PX = 16;
// Keyboard: ← → move by one bar, ↑ ↓ zoom like one wheel notch
export const KEYBOARD_PAN_BARS = 1;

// ─── Inertia of the pan (kinetic scroll as in lightweight-charts) ───────────
// Speeds in CSS px per millisecond
export const KINETIC_MIN_START_SPEED = 0.2;
export const KINETIC_MAX_SPEED = 7;
export const KINETIC_STOP_SPEED = 0.02;
// Speed multiplier per millisecond of the animation
export const KINETIC_DAMPING_PER_MS = 0.997;
// The release speed is measured over the last moves within this window
export const KINETIC_SAMPLE_WINDOW_MS = 100;
